import React from 'react';
import { Drawer, Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { GuideForm } from '@/features/guides/components/GuideForm';
import { type GuideFormData } from '@/features/guides/components/GuideForm';
import { type GuideContentRequest, type VehicleFilterRequest } from '@/features/guides/api/generated';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';

interface CreateGuideDrawerProps {
  open: boolean;
  onClose: () => void;
  formState: GuideFormData;
  isDirty: boolean;
  isCreating: boolean;
  createNewGuide: () => Promise<void>;
  updateContentField: (key: keyof GuideContentRequest, value: unknown) => void;
  updateRootVehicleWithSelection: (selection: VehicleSelectionValue) => void;
  addSolution: () => void;
  updateSolution: (index: number, value: string) => void;
  removeSolution: (index: number) => void;
  reorderSolutions: (fromIndex: number, toIndex: number) => void;
  addFilter: (filter: VehicleFilterRequest) => void;
  removeFilterAt: (index: number) => void;
  updateFilterAt: (index: number, filter: VehicleFilterRequest) => void;
}

export const CreateGuideDrawer: React.FC<CreateGuideDrawerProps> = ({
  open,
  onClose,
  formState,
  isDirty,
  isCreating,
  createNewGuide,
  updateContentField,
  updateRootVehicleWithSelection,
  addSolution,
  updateSolution,
  removeSolution,
  reorderSolutions,
  addFilter,
  removeFilterAt,
  updateFilterAt,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Drawer
        title={t('guides.createNewGuide')}
        placement="right"
        width={600}
        open={open}
        onClose={onClose}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose}>{t('common.cancel')}</Button>
              <Button 
                type="primary" 
                loading={isCreating} 
                onClick={createNewGuide} 
                disabled={!isDirty || isCreating}
              >
                {t('guides.create')}
              </Button>
            </Space>
          </div>
        }
      >
        <GuideForm
          formData={formState}
          onUpdateContent={updateContentField}
          onUpdateRootVehicle={updateRootVehicleWithSelection}
          onAddSolution={addSolution}
          onUpdateSolution={updateSolution}
          onRemoveSolution={removeSolution}
          onReorderSolutions={reorderSolutions}
          onAddFilter={addFilter}
          onRemoveFilter={removeFilterAt}
          onUpdateFilter={updateFilterAt}
          mode="dashboard"
        />
      </Drawer>
    </>
  );
};